import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { Level } from '../models/ScoresResponse';
import { Module } from '../models/Module';
import { Role } from '../models/Role';
import { MetaModule } from '../models/MetaModule';
import { ModuleAccess } from '../models/ModuleAccess';
import { UserTier } from '../models/User';

@Injectable({
  providedIn: 'root',
})
export class ModulesService {
  constructor(private http: HttpClient) {}

  getModuleLevels(): Observable<Level[]> {
    const url = `/api/v1/modules/levels`;

    return this.http.get<Level[]>(url);
  }

  getModules(role: Role): Observable<Module[]> {
    const url = `/api/v1/modules/role/${role}`;

    return this.http.get<Module[]>(url);
  }

  getAllModules(): Observable<Module[]> {
    const url = `/api/v1/modules/`;

    return this.http.get<Module[]>(url);
  }

  getMetaModules(): Observable<MetaModule[]> {
    const url = `/api/v1/modules/meta`;

    return this.http.get<MetaModule[]>(url);
  }

  getModulesAccess(): Observable<ModuleAccess[]> {
    const url = `/api/v1/modules/access`;

    return this.http.get<ModuleAccess[]>(url);
  }

  getNewModuleBgImgPath(name: string, color: string | undefined, take: boolean = false): string {
    const takePath = take ? '_take' : '';

    if (color === undefined) {
      return `assets/new_module${takePath}_backgrounds/${name}.svg`;
    } else {
      return `assets/new_module${takePath}_backgrounds/tp/${color}.svg`;
    }
  }

  getModuleBgImgPath(name: string, color: string | undefined): string {
    if (color === undefined) {
      return `assets/module_backgrounds/${name}.png`;
    } else {
      return `assets/module_backgrounds/tp/${color}.png`;
    }
  }

  getModuleTakeBgImgPath(name: string, color: string | undefined): string {
    if (color === undefined) {
      return `assets/module_take_backgrounds/${name}.png`;
    } else {
      return `assets/module_take_backgrounds/tp/${color}.png`;
    }
  }

  getModuleColorName(name: string, colorName: string | undefined): string {
    if (colorName) {
      return this.getMetaColorFromBgColor(colorName);
    }

    let color = '';

    if (name === 'decrypter_info') {
      color = '#ff9024';
    } else if (name === 'identite_numerique') {
      color = '#3abcd8';
    } else if (name === 'eco_responsabilite') {
      color = '#0fc461';
    } else if (name === 'intelligence_artificielle') {
      color = '#5a36b2';
    } else if (name === 'numerique_attitude') {
      color = '#ec407a';
    } else if (name === 'reseaux_sociaux') {
      color = '#c271e7';
    } else if (name === 'cyber_harcelement') {
      color = '#f4b60b';
    } else if (name === 'dependance_numerique') {
      color = '#007eff';
    } else if (name === 'esprit_critique') {
      color = '#d81b60';
    }

    return color;
  }

  getMetaBgColor(metaId: number): string {
    if (metaId === 1) {
      return 'blue';
    } else if (metaId === 2) {
      return 'apple';
    } else if (metaId === 3) {
      return 'prune';
    } else {
      // 4
      return 'coral';
    }
  }

  getMetaColorFromBgColor(color: string): string {
    if (color === 'blue') {
      return '#008fb2';
    } else if (color === 'apple') {
      return '#d3dd18';
    } else if (color === 'prune') {
      return '#9937a2';
    } else {
      // coral
      return '#fa897b';
    }
  }

  getMetaModuleColor(metaId: number, i: number): string {
    if (metaId === 1) {
      // 1 - Comprendre et encadrer
      const array: string[] = ['#008fb2', '#0191B4', '#25848e', '#2ea4b0', '#35bbc4', '#3ebecc', '#62cad6', '#86d6e0', '#aae3e9', '#cfeff3'];

      return array[i];
    } else if (metaId === 2) {
      // 2 - Outils et tendances de la generation Z
      const array: string[] = ['#d3dd18', '#769501', '#757b0c', '#9ba210', '#c0c915', '#dee723', '#e4ed4b', '#eaf274', '#f1f79d', '#f7fbc7'];

      return array[i];
    } else {
      // 3 - Parents et enseignants à l'ère de l'IA
      const array: string[] = ['#9937a2', '#a02b93', '#602165', '#7f2d86', '#9d38a6', '#b74ac1', '#c36ccc', '#d08ed7', '#ddb0e3', '#ebd2ef'];

      return array[i];
    }

    //const array: string[] = ['#fa897b', '#e88992', '#821405', '#ac1a06', '#d61f08', '#f62d14', '#f75741', '#f87f6e', '#f9a69a', '#fbccc5']
  }

  getModuleBgColorName(module: Module, color: string | undefined): string {
    return this.getModuleColorName(module.module_enum, color);
  }

  async canSelectModule(userTier: UserTier, level: Level, moduleEnum: string): Promise<boolean> {
    if (userTier === UserTier.premium) {
      return true;
    }

    const moduleAccess: ModuleAccess[] = await firstValueFrom(this.getModulesAccess());
    const access = moduleAccess.find((elt) => elt.module_enum === moduleEnum && elt.level === level);
    if (!access) {
      return false;
    }

    return access.tier === UserTier.free;
  }
}
